import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, useAdminApi } from "@/lib/api";
import { RESOURCES } from "./resources";
import RecordForm, { getPath } from "./RecordForm";
import ScorecardEditor from "./ScorecardEditor";

const strip = ({ id, created_at, updated_at, registration_id, ...rest }) => rest;

export default function ContentManager() {
  const { coll } = useParams();
  const cfg = RESOURCES[coll];
  const req = useAdminApi();
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [venues, setVenues] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setItems(await req("get", `/admin/content/${coll}`)); } catch (e) { toast.error(errMsg(e)); }
  }, [req, coll]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/venues").then((r) => setVenues(r.data));
  }, [coll]);

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";
  const display = (row, k) => {
    const v = getPath(row, k);
    if (k.endsWith("team_id") || k === "team_a_id" || k === "team_b_id") return v ? teamName(v) : "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return v === "" || v == null ? "—" : String(v).replace(/_/g, " ");
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = strip(edit.data);
      if (edit.id) await req("put", `/admin/content/${coll}/${edit.id}`, body);
      else await req("post", `/admin/content/${coll}`, body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try { await req("delete", `/admin/content/${coll}/${row.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!cfg) return <p>Unknown section.</p>;
  const sorted = [...items].sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  return (
    <div data-testid={`content-${coll}`} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-display text-4xl uppercase">{cfg.title}</h1><p className="text-sm text-slate-400">{items.length} record(s)</p></div>
        {!cfg.noCreate && (
          <button type="button" data-testid="content-add-btn" onClick={() => setEdit({ data: { ...cfg.defaults, ...(coll === "matches" ? { number: items.length + 1 } : {}) } })}
            className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-[#0A192F]"><Plus className="h-4 w-4" />Add {cfg.single}</button>
        )}
      </div>
      <div className="overflow-x-auto rounded-md border border-white/10">
        <table data-testid="content-table" className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-[#112240] text-[11px] uppercase tracking-widest text-slate-400"><tr>{cfg.columns.map(([, l]) => <th key={l} className="p-3">{l}</th>)}<th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-white/5">
            {sorted.map((row) => (
              <tr key={row.id} data-testid={`content-row-${row.id}`} className="hover:bg-white/5">
                {cfg.columns.map(([k]) => <td key={k} className="p-3">{display(row, k)}</td>)}
                <td className="p-3 text-right">
                  <button type="button" aria-label="Edit" data-testid={`content-edit-${row.id}`} onClick={() => setEdit({ id: row.id, data: row })} className="p-2 text-slate-300 hover:text-[#00E5FF]"><Pencil className="h-4 w-4" /></button>
                  {!cfg.noCreate && <button type="button" aria-label="Delete" data-testid={`content-delete-${row.id}`} onClick={() => remove(row)} className="p-2 text-slate-300 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={cfg.columns.length + 1} className="p-10 text-center text-slate-400">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="content-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#112240] text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{edit?.id ? "Edit" : "Add"} {cfg.single}</DialogTitle></DialogHeader>
          {edit && (cfg.scorecard ? (
            <Tabs defaultValue="details">
              <TabsList className="bg-[#0A192F]"><TabsTrigger value="details" data-testid="match-tab-details">Details</TabsTrigger><TabsTrigger value="scorecard" data-testid="match-tab-scorecard">Scorecard</TabsTrigger></TabsList>
              <TabsContent value="details" className="mt-4"><RecordForm fields={cfg.fields} value={edit.data} onChange={(d) => setEdit((e) => ({ ...e, data: d }))} teams={teams} venues={venues} /></TabsContent>
              <TabsContent value="scorecard" className="mt-4"><ScorecardEditor match={edit.data} teams={teams} onChange={(d) => setEdit((e) => ({ ...e, data: d }))} /></TabsContent>
            </Tabs>
          ) : <RecordForm fields={cfg.fields} value={edit.data} onChange={(d) => setEdit((e) => ({ ...e, data: d }))} teams={teams} venues={venues} />)}
          <div className="mt-4 flex justify-end gap-3">
            <button type="button" onClick={() => setEdit(null)} className="rounded-full border border-white/20 px-5 py-2 text-xs font-bold uppercase tracking-wider">Cancel</button>
            <button type="button" disabled={busy} onClick={save} data-testid="content-save-btn" className="rounded-full bg-[#00E5FF] px-6 py-2 text-xs font-bold uppercase tracking-wider text-[#0A192F] disabled:opacity-60">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
